import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';
import { useModulesStore } from 'src/context/moduleContext';
import type { useGetFormCustomisationByParentTypes } from 'src/hooks/queries/form-configuration/useGetFormCustomisationByParentTypes';
import type { useGetReportingData } from 'src/hooks/queries/reporting/useGetReportingData';
import type { ReportField } from 'src/pages/dashboards/custom-data-source-widget/widget-chart/types';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { TypedCustomDatasource } from '../types';
import { reconcileContentDisplay } from '../update/contentDisplay';
import { CustomDatasourceModel } from '../update/customDatasourceModel';
import { displayTypes } from '../update/display-types';
import type { CustomAttributeSchemaLookup } from '../update/types';
import { useCustomDatasourceHelpers } from '../useCustomDatasourceHelpers';

type GetReportingDataData = NonNullable<
  ReturnType<typeof useGetReportingData>['data']
>;

type GetFormCustomisationData = NonNullable<
  ReturnType<typeof useGetFormCustomisationByParentTypes>['data']
>;

export const useCustomDatasourceExporterMapper = ({
  customDatasource,
  customAttributeSchemaLookup,
  formFieldConfigurations,
  contentDisplay,
}: {
  customDatasource:
    | null
    | Pick<TypedCustomDatasource, 'Datasources' | 'Fields'>
    | undefined;
  customAttributeSchemaLookup: CustomAttributeSchemaLookup | null | undefined;
  formFieldConfigurations:
    | GetFormCustomisationData['form_field_configuration']
    | null;
  /**
   * The widget's persisted Cloudscape column preferences (order + visibility).
   * When provided, the export mirrors the on-screen table: columns are emitted
   * in `contentDisplay` order and hidden (`visible === false`) columns are
   * dropped. When absent, all fields export in model order (legacy behaviour).
   */
  contentDisplay?:
    | readonly CollectionPreferencesProps.ContentDisplayItem[]
    | undefined;
}) => {
  const helpers = useCustomDatasourceHelpers();
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const modules = useModulesStore((s) => s.modules);

  return (results: GetReportingDataData['reportingData']) => {
    if (!customDatasource) {
      throw new Error('Missing custom data source');
    }
    if (!customAttributeSchemaLookup) {
      throw new Error('Missing custom attribute schema');
    }
    const customDatasourceModel = CustomDatasourceModel(
      customDatasource,
      customAttributeSchemaLookup,
      formFieldConfigurations,
      formSectionsEnabled,
      modules
    );
    const fields = customDatasourceModel.fields;

    // CUS-207: fields whose custom attribute was deleted are kept as positional
    // placeholders (aligned with the backend's NULL cell) but must not appear as
    // empty columns in the CSV — mirror the on-screen table and drop them, while
    // still indexing surviving columns by their original model position.
    const deletedFieldValues = new Set(
      customDatasourceModel.deletedFields.map((f) => f.value)
    );

    // Column emission order. Reporting rows are positional (`r[i]` ↔ `fields[i]`),
    // so the export walks a list of model indices rather than the fields directly.
    // With `contentDisplay` we mirror the on-screen table: reconcile against the
    // current field selection (same helper the table uses, for parity), drop
    // hidden columns, and map each ordered id back to its model index. Duplicate
    // ids in `contentDisplay` re-emit the same column positionally. Without
    // `contentDisplay` we fall back to model order over every field.
    const orderedIndices: number[] = (() => {
      if (!contentDisplay) {
        return fields.map((_, i) => i);
      }
      const indexByValue = new Map<string, number>();
      fields.forEach((f, i) => {
        if (!indexByValue.has(f.value)) {
          indexByValue.set(f.value, i);
        }
      });
      const reconciled = reconcileContentDisplay(
        contentDisplay,
        fields.map((f) => f.value)
      );

      return (reconciled ?? [])
        .filter((item) => item.visible !== false)
        .map((item) => indexByValue.get(item.id))
        .filter((i): i is number => i !== undefined);
    })().filter((i) => !deletedFieldValues.has(fields[i].value));

    const headers = orderedIndices.map(
      (i) => fields[i].label ?? fields[i].defaultLabel
    );
    const dataAndHeaders = [
      headers,
      ...(results?.map((r) =>
        orderedIndices.map((i) => {
          const fieldDef = fields[i];
          const displayType = displayTypes[fieldDef.displayType];
          const formatter = displayType.exportVal;
          const label =
            formatter({
              fieldData: r[i] as ReportField,
              fieldDef,
              helpers,
              groupByDatePrecision: null,
            }) ?? '';

          return label;
        })
      ) ?? []),
    ];

    return dataAndHeaders;
  };
};
